<template>
  <div class="image-annotator">
    <div class="ann-toolbar">
      <el-radio-group v-model="tool" size="small" :disabled="disabled">
        <el-radio-button label="rect">框选</el-radio-button
        ><el-radio-button label="arrow">箭头</el-radio-button
        ><el-radio-button label="text">文字</el-radio-button
        ><el-radio-button label="select">选择</el-radio-button>
      </el-radio-group>
      <div class="ann-colors">
        <button
          v-for="color in COLORS"
          :key="color"
          type="button"
          class="ann-color"
          :class="{ 'is-active': color === activeColor }"
          :style="{ background: color }"
          :aria-label="`颜色 ${color}`"
          @click="activeColor = color"
        />
      </div>
      <el-button size="small" :disabled="disabled || selectedIndex < 0" @click="removeSelected"
        >删除选中</el-button
      >
      <el-button size="small" :disabled="disabled || !currentAnnotations.length" @click="clearAll"
        >清空</el-button
      >
      <slot name="toolbar-extra" />
    </div>
    <div v-loading="loading" class="ann-stage-wrap">
      <canvas
        ref="stage"
        class="ann-stage"
        @mousedown="startDrawing"
        @mousemove="drawPreview"
        @mouseup="finishDrawing"
        @mouseleave="finishDrawing"
      />
    </div>
    <div v-if="currentAnnotations.length" class="ann-list">
      <span v-for="annotation in currentAnnotations" :key="annotation.id" class="ann-chip"
        ><i class="ann-chip-dot" :style="{ background: annotation.color }" /><span>{{
          annLabel(annotation)
        }}</span
        ><button type="button" class="ann-chip-del" @click="removeById(annotation.id)">
          ×
        </button></span
      >
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import type { ImageAnnotation, ImageAnnotationTool } from './types';

type Tool = ImageAnnotationTool | 'select';

export default Vue.extend({
  name: 'ImageAnnotator',
  props: {
    imageUrl: { type: String, required: true },
    value: { type: Array as () => ImageAnnotation[], default: () => [] },
    annotations: { type: Array as () => ImageAnnotation[], default: null },
    disabled: { type: Boolean, default: false },
    stageW: { type: Number, default: 720 },
    stageH: { type: Number, default: 540 },
  },
  data() {
    return {
      COLORS: ['#E5484D', '#3B6FE0', '#22A06B', '#D9822B', '#1F2733'],
      tool: 'rect' as Tool,
      activeColor: '#E5484D',
      loading: false,
      selectedIndex: -1,
      drawing: false,
      start: null as { x: number; y: number } | null,
      image: null as HTMLImageElement | null,
      preview: null as ImageAnnotation | null,
      sequence: 0,
    };
  },
  computed: {
    currentAnnotations(): ImageAnnotation[] {
      return (this.annotations == null ? this.value : this.annotations) as ImageAnnotation[];
    },
  },
  mounted() {
    this.loadImage();
    window.addEventListener('resize', this.resizeCanvas);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCanvas);
  },
  watch: {
    imageUrl() {
      this.loadImage();
    },
    currentAnnotations: {
      deep: true,
      handler(this: any) {
        this.paint();
      },
    },
  },
  methods: {
    emitAnnotations(items: ImageAnnotation[]) {
      this.$emit('input', items);
      this.$emit('update:annotations', items);
    },
    loadImage() {
      this.loading = true;
      const image = new Image();
      image.onload = () => {
        this.image = image;
        this.loading = false;
        this.resizeCanvas();
      };
      image.onerror = () => {
        this.loading = false;
      };
      if (!this.imageUrl.startsWith('data:') && !this.imageUrl.startsWith('blob:'))
        image.crossOrigin = 'anonymous';
      image.src = this.imageUrl;
    },
    canvas(): HTMLCanvasElement {
      return this.$refs.stage as HTMLCanvasElement;
    },
    resizeCanvas() {
      if (!this.image) return;
      const parent = this.canvas().parentElement;
      const width = Math.min(this.stageW, parent ? parent.clientWidth : this.stageW);
      const ratio = this.image.width ? this.image.height / this.image.width : 0.75;
      this.canvas().width = Math.max(240, width);
      this.canvas().height = Math.min(
        this.stageH,
        Math.max(160, Math.round(this.canvas().width * ratio))
      );
      this.paint();
    },
    point(event: MouseEvent) {
      const rect = this.canvas().getBoundingClientRect();
      return {
        x: (event.clientX - rect.left) / rect.width,
        y: (event.clientY - rect.top) / rect.height,
      };
    },
    startDrawing(event: MouseEvent) {
      if (this.disabled || this.tool === 'select') return;
      const point = this.point(event);
      if (this.tool === 'text') {
        const text = window.prompt('请输入标注文字', '标注');
        if (text)
          this.addAnnotation({
            id: this.nextId(),
            tool: 'text',
            points: [point.x, point.y, 0, 0],
            color: this.activeColor,
            text,
          });
        return;
      }
      this.start = point;
      this.drawing = true;
    },
    drawPreview(event: MouseEvent) {
      if (!this.drawing || !this.start) return;
      const point = this.point(event);
      this.preview = {
        id: 'preview',
        tool: this.tool as ImageAnnotationTool,
        points: [this.start.x, this.start.y, point.x, point.y],
        color: this.activeColor,
      };
      this.paint();
    },
    finishDrawing() {
      if (!this.drawing || !this.preview) return;
      const preview = this.preview;
      if (
        Math.abs(preview.points[2] - preview.points[0]) > 0.01 ||
        Math.abs(preview.points[3] - preview.points[1]) > 0.01
      )
        this.addAnnotation({ ...preview, id: this.nextId() });
      this.drawing = false;
      this.start = null;
      this.preview = null;
      this.paint();
    },
    addAnnotation(annotation: ImageAnnotation) {
      this.emitAnnotations(this.currentAnnotations.concat(annotation));
    },
    nextId() {
      this.sequence += 1;
      return `ann-${Date.now()}-${this.sequence}`;
    },
    removeSelected() {
      if (this.selectedIndex >= 0) {
        const items = this.currentAnnotations.slice();
        items.splice(this.selectedIndex, 1);
        this.emitAnnotations(items);
        this.selectedIndex = -1;
      }
    },
    removeById(id: string) {
      this.emitAnnotations(this.currentAnnotations.filter((annotation) => annotation.id !== id));
    },
    clearAll() {
      this.emitAnnotations([]);
      this.selectedIndex = -1;
    },
    annLabel(annotation: ImageAnnotation) {
      return annotation.tool === 'rect'
        ? '框选'
        : annotation.tool === 'arrow'
          ? '箭头'
          : `文字：${annotation.text || ''}`;
    },
    paint() {
      const canvas = this.$refs.stage as HTMLCanvasElement;
      if (!canvas || !this.image) return;
      const context = canvas.getContext('2d');
      if (!context) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(this.image, 0, 0, canvas.width, canvas.height);
      this.currentAnnotations
        .concat(this.preview ? [this.preview] : [])
        .forEach((annotation) =>
          this.paintAnnotation(context, annotation, canvas.width, canvas.height)
        );
    },
    paintAnnotation(
      context: CanvasRenderingContext2D,
      annotation: ImageAnnotation,
      width: number,
      height: number
    ) {
      const [x1, y1, x2, y2] = annotation.points;
      context.strokeStyle = annotation.color;
      context.fillStyle = annotation.color;
      context.lineWidth = 2;
      if (annotation.tool === 'rect')
        context.strokeRect(x1 * width, y1 * height, (x2 - x1) * width, (y2 - y1) * height);
      else if (annotation.tool === 'arrow') {
        context.beginPath();
        context.moveTo(x1 * width, y1 * height);
        context.lineTo(x2 * width, y2 * height);
        context.stroke();
        const angle = Math.atan2(y2 - y1, x2 - x1);
        context.beginPath();
        context.moveTo(x2 * width, y2 * height);
        context.lineTo(
          x2 * width - 10 * Math.cos(angle - Math.PI / 6),
          y2 * height - 10 * Math.sin(angle - Math.PI / 6)
        );
        context.lineTo(
          x2 * width - 10 * Math.cos(angle + Math.PI / 6),
          y2 * height - 10 * Math.sin(angle + Math.PI / 6)
        );
        context.closePath();
        context.fill();
      } else {
        context.font = '600 18px sans-serif';
        context.fillText(annotation.text || '', x1 * width, y1 * height + 18);
      }
    },
    exportImage() {
      return this.canvas().toDataURL('image/jpeg', 0.85);
    },
  },
});
</script>

<style lang="scss" scoped>
.image-annotator {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ann-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.ann-colors {
  display: flex;
  gap: 6px;
}
.ann-color {
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
}
.ann-color.is-active {
  border-color: $color-text;
}
.ann-stage-wrap {
  display: flex;
  justify-content: center;
  min-height: 160px;
  overflow: hidden;
}
.ann-stage {
  display: block;
  max-width: 100%;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  cursor: crosshair;
}
.ann-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ann-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border: 1px solid $color-border;
  border-radius: 999px;
  font-size: 12px;
}
.ann-chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.ann-chip-del {
  border: 0;
  background: transparent;
  cursor: pointer;
  color: $color-text-secondary;
}
</style>
